import React from 'react';
import { Bot } from '@/types/Bot';
import { BotCard } from './BotCard';
import { Skeleton } from '@/components/ui/skeleton';
import { motion } from 'framer-motion';

interface ResultsGridProps {
  bots: Bot[];
  loading?: boolean;
  onBotClick?: (bot: Bot) => void;
  onToggleFavorite?: (botId: string) => void;
  isFavorite?: (botId: string) => boolean;
  // Comparison props
  isInComparison?: (botId: string) => boolean;
  canAddToComparison?: boolean;
  onAddToComparison?: (bot: Bot) => void;
  onRemoveFromComparison?: (botId: string) => void;
}

export const ResultsGrid: React.FC<ResultsGridProps> = ({ 
  bots, 
  loading = false, 
  onBotClick, 
  onToggleFavorite, 
  isFavorite,
  isInComparison,
  canAddToComparison = true,
  onAddToComparison,
  onRemoveFromComparison
}) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
        {Array.from({ length: 8 }).map((_, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            className="glass rounded-lg border border-border p-6 space-y-4"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 space-y-3">
                <Skeleton className="h-6 w-3/4" />
                <div className="flex gap-2">
                  <Skeleton className="h-5 w-16" />
                  <Skeleton className="h-5 w-20" />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-4 w-16" />
            </div>
            <Skeleton className="h-12 w-full" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-20" />
              <div className="flex flex-wrap gap-2">
                <Skeleton className="h-6 w-16" />
                <Skeleton className="h-6 w-20" />
                <Skeleton className="h-6 w-16" />
              </div>
            </div>
            <div className="flex gap-2">
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
            </div>
          </motion.div>
        ))}
      </div>
    );
  }

  if (bots.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="max-w-md mx-auto space-y-4">
          <div className="w-16 h-16 mx-auto bg-muted rounded-full flex items-center justify-center opacity-50">
            <span className="text-2xl">🤖</span>
          </div>
          <h3 className="text-xl font-semibold text-foreground">No AI bots found</h3>
          <p className="text-muted-foreground">
            Try adjusting your search terms or filters to find the perfect AI assistant.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
      {bots.map((bot, index) => (
        <motion.div 
          key={bot.id} 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: index * 0.05 }}
        >
          <BotCard 
            bot={bot} 
            onBotClick={onBotClick}
            onToggleFavorite={onToggleFavorite}
            isFavorite={isFavorite?.(bot.id)}
            isInComparison={isInComparison?.(bot.id)}
            canAddToComparison={canAddToComparison}
            onAddToComparison={onAddToComparison}
            onRemoveFromComparison={onRemoveFromComparison}
          />
        </motion.div>
      ))}
    </div>
  );
};